import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { style: {"display":"none"} }

import { onMounted, onBeforeUnmount, watch, h, nextTick, computed } from 'vue';
import { toast, ToastTransition, type CloseButtonProps } from 'vue3-toastify';
import { useAppStore } from '@/stores/app.store';


export default /*@__PURE__*/_defineComponent({
  __name: 'Toast',
  props: {
    title: {},
    type: { default: 'info' },
    description: { default: undefined },
    closable: { type: Boolean },
    autoClose: { type: [Number, Boolean], default: 10000 },
    position: { default: 'top-right' },
    draggable: { type: Boolean, default: true },
    animation: { default: 'zoom' },
    id: { default: undefined },
    iconClass: { default: undefined }
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const appStore = useAppStore();

const isDraggable = computed(() => {
    // If draggable is explicitly set to false, always respect it
    if (props.draggable === false) {
        return false;
    }

    // Otherwise, force draggable on for mobile, or use provided value
    return appStore.isMobile ? true : props.draggable;
});

// Check if toast should have default cursor (not draggable, not closable, autoClose = 0)
const shouldHaveDefaultCursor = computed(() => {
    return !isDraggable.value && !props.closable && (props.autoClose === 0 || props.autoClose === false);
});

// Check if icon should be centered (no title and no close button)
const shouldCenterIcon = computed(() => {
    const hasTitle = props.title && props.title.trim().length > 0;
    return !hasTitle && !props.closable;
});

// Map type to vue3-toastify type
const getToastType = (): 'info' | 'success' | 'error' | 'warning' | 'default' => {
    if (props.type === 'neutral') {
        return 'default';
    }
    return props.type || 'info';
};

// Determine size based on description presence
const getSize = (): 'large' | 'small' => {
    return props.description ? 'large' : 'small';
};

// Build icon VNode with custom classes based on size and type
// Note: vue3-toastify's icon prop requires a VNode, not an HTML string
const getIcon = () => {
    // If custom iconClass is provided, use it
    if (props.iconClass) {
        // Ensure cwx-icon class is present
        // Convert to lowercase, split by space, and check if array includes "cwx-icon" as exact match
        const classArray = props.iconClass.toLowerCase().split(/\s+/).filter(Boolean);
        const hasCwxIcon = classArray.includes('cwx-icon');
        const iconClass = hasCwxIcon 
            ? props.iconClass 
            : `cwx-icon ${props.iconClass}`;
        return h('i', {
            class: iconClass
        });
    }

    // Otherwise, use default icons based on type
    const type = props.type;

    switch (type) {
        case 'info':
            return h('i', {
                class: 'cwx-icon cwx-icon-before_information-default'
            });
        case 'success':
            return h('i', {
                class: 'cwx-icon cwx-icon-before_check-outline'
            });
        case 'error':
            return h('i', {
                class: 'cwx-icon cwx-icon-before_close-outline'
            });
        case 'warning':
            return h('i', {
                class: 'cwx-icon cwx-icon-before_warning-active'
            });
        case 'neutral':
            return h('i', {
                class: 'cwx-icon cwx-icon-before_question-default'
            });
        default:
            return h('i', {
                class: 'cwx-icon cwx-icon-before_information-default'
            });
    }

};

// Build custom close button with cwx-icon classes
const getCloseButton = (closeButtonProps: CloseButtonProps) => {
    return h('button', {
        class: 'Toastify__close-button cwx-icon cwx-icon-before_close',
        type: 'button',
        'aria-label': closeButtonProps.ariaLabel || 'close',
        onClick: closeButtonProps.closeToast
    });
};

// Build toast content based on description presence
const getToastContent = (): string => {
    const escapedTitle = props.title;

    if (props.description) {
        const escapedDescription = props.description;
        return `<div class="toast-content">
      <div class="toast-title">${escapedTitle}</div>
      <div class="toast-description">${escapedDescription}</div>
    </div>`;
    }
    return `<div class="toast-content">
    <div class="toast-title">${escapedTitle}</div>
  </div>`;
};

let toastId: string | number | undefined;
let toastElement: HTMLElement | null = null;
let dragCleanup: (() => void) | null = null;
let mutationObserver: MutationObserver | null = null;
let progressBarObserver: MutationObserver | null = null;

// Generate a unique ID for this component instance, or use the provided id prop
const instanceId = computed(() => {
    return props.id || `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
});

// Update aria-hidden attribute on progress bar based on opacity
const updateProgressBarAriaHidden = (progressBar: HTMLElement) => {
    const opacity = window.getComputedStyle(progressBar).opacity;
    const isVisible = parseFloat(opacity) > 0;
    progressBar.setAttribute('aria-hidden', String(!isVisible));
};

// Setup accessibility for progress bar
const setupProgressBarAccessibility = (element: HTMLElement) => {
    const progressBar = element.querySelector('.Toastify__progress-bar') as HTMLElement;
    if (!progressBar) return;

    // Initial update
    updateProgressBarAriaHidden(progressBar);

    // Clean up existing observer if any
    if (progressBarObserver) {
        progressBarObserver.disconnect();
        progressBarObserver = null;
    }

    // Watch for style attribute changes (inline style changes)
    progressBarObserver = new MutationObserver(() => {
        updateProgressBarAriaHidden(progressBar);
    });

    progressBarObserver.observe(progressBar, {
        attributes: true,
        attributeFilter: ['style'],
        subtree: false
    });

    // Also check periodically for computed style changes (from CSS classes/animations)
    // Use a reasonable interval to avoid performance issues
    let intervalId: number | null = null;
    const checkInterval = 100; // Check every 100ms

    const startPeriodicCheck = () => {
        if (intervalId !== null) return;
        intervalId = window.setInterval(() => {
            if (progressBar.isConnected) {
                updateProgressBarAriaHidden(progressBar);
            } else {
                // Clean up if element is removed from DOM
                if (intervalId !== null) {
                    clearInterval(intervalId);
                    intervalId = null;
                }
                if (progressBarObserver) {
                    progressBarObserver.disconnect();
                    progressBarObserver = null;
                }
            }
        }, checkInterval);
    };

    startPeriodicCheck();

    // Store cleanup function
    const originalDisconnect = progressBarObserver.disconnect.bind(progressBarObserver);
    progressBarObserver.disconnect = () => {
        originalDisconnect();
        if (intervalId !== null) {
            clearInterval(intervalId);
            intervalId = null;
        }
    };
};

// Drag functionality
const makeToastDraggable = (element: HTMLElement) => {
    if (!isDraggable.value) return;

    // Mark this element as having drag handlers to avoid duplicate setup
    if (element.dataset.dragHandlersAttached === 'true') {
        return;
    }
    element.dataset.dragHandlersAttached = 'true';

    let isDragging = false;
    let hasMoved = false;
    let startX = 0;
    let startY = 0;
    let initialX = 0;
    let initialY = 0;
    let currentX = 0;
    let currentY = 0;
    const DRAG_THRESHOLD = 5; // Minimum pixels to move before considering it a drag

    // Get initial position from computed styles or default
    const getInitialPosition = () => {
        const rect = element.getBoundingClientRect();
        return {
            x: rect.left,
            y: rect.top
        };
    };

    // Helper to get coordinates from both mouse and touch events
    const getEventCoordinates = (e: MouseEvent | TouchEvent): { clientX: number; clientY: number } => {
        // Avoid referencing global TouchEvent (can be undefined in some browsers/contexts, e.g. Firefox desktop)
        if ('touches' in e) {
            const touchEvent = e as TouchEvent;
            const touch = touchEvent.touches[0] || touchEvent.changedTouches[0];
            return { clientX: touch.clientX, clientY: touch.clientY };
        }
        return { clientX: e.clientX, clientY: e.clientY };
    };

    const startDrag = (e: MouseEvent | TouchEvent) => {
        // Don't start drag if clicking on close button or progress bar
        const target = e.target as HTMLElement;
        if (target && (target.closest('.Toastify__close-button') || target.closest('.Toastify__progress-bar'))) {
            return;
        }

        hasMoved = false;
        const pos = getInitialPosition();
        initialX = pos.x;
        initialY = pos.y;
        const coords = getEventCoordinates(e);
        startX = coords.clientX;
        startY = coords.clientY;

        element.style.cursor = 'grabbing';
        element.style.userSelect = 'none';
        element.style.touchAction = 'none'; // Prevent default touch behaviors

        // Add both mouse and touch event listeners
        document.addEventListener('mousemove', drag);
        document.addEventListener('mouseup', stopDrag);
        document.addEventListener('touchmove', drag, { passive: false });
        document.addEventListener('touchend', stopDrag);
        document.addEventListener('touchcancel', stopDrag);

        e.preventDefault();
    };

    const drag = (e: MouseEvent | TouchEvent) => {
        const coords = getEventCoordinates(e);
        const deltaX = Math.abs(coords.clientX - startX);
        const deltaY = Math.abs(coords.clientY - startY);

        // Only start dragging if moved beyond threshold
        if (!hasMoved && (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD)) {
            hasMoved = true;
            isDragging = true;

            // For centered toasts, the container uses transform: translateX(-50%), which makes
            // position:fixed on the toast use the container as containing block. Move the toast
            // to body so fixed positioning uses viewport coordinates and the toast doesn't jump.
            const parent = element.parentElement;
            if (parent?.closest('.Toastify__toast-container--top-center, .Toastify__toast-container--bottom-center')) {
                document.body.appendChild(element);
            }

            element.style.position = 'fixed';
            element.style.left = `${initialX}px`;
            element.style.top = `${initialY}px`;
            element.style.right = 'auto';
            element.style.transform = 'none';
            element.style.zIndex = '10000';
        }

        if (!isDragging) return;

        e.preventDefault();
        currentX = initialX + (coords.clientX - startX);
        currentY = initialY + (coords.clientY - startY);

        // Constrain to viewport
        const rect = element.getBoundingClientRect();
        const maxX = window.innerWidth - rect.width;
        const maxY = window.innerHeight - rect.height;

        currentX = Math.max(0, Math.min(currentX, maxX));
        currentY = Math.max(0, Math.min(currentY, maxY));

        element.style.left = `${currentX}px`;
        element.style.top = `${currentY}px`;
    };

    const stopDrag = () => {
        if (isDragging) {
            isDragging = false;
        }
        hasMoved = false;
        element.style.cursor = 'grab';
        element.style.userSelect = '';
        element.style.touchAction = '';

        // Remove both mouse and touch event listeners
        document.removeEventListener('mousemove', drag);
        document.removeEventListener('mouseup', stopDrag);
        document.removeEventListener('touchmove', drag);
        document.removeEventListener('touchend', stopDrag);
        document.removeEventListener('touchcancel', stopDrag);
    };

    element.style.cursor = 'grab';
    element.style.touchAction = 'none'; // Prevent default touch behaviors
    element.addEventListener('mousedown', startDrag);
    element.addEventListener('touchstart', startDrag, { passive: false });

    // Cleanup function
    dragCleanup = () => {
        element.removeEventListener('mousedown', startDrag);
        element.removeEventListener('touchstart', startDrag);
        document.removeEventListener('mousemove', drag);
        document.removeEventListener('mouseup', stopDrag);
        document.removeEventListener('touchmove', drag);
        document.removeEventListener('touchend', stopDrag);
        document.removeEventListener('touchcancel', stopDrag);
        element.style.cursor = '';
        element.style.userSelect = '';
        element.style.touchAction = '';
        element.style.position = '';
        element.style.left = '';
        element.style.top = '';
        element.style.right = '';
        element.style.transform = '';
        element.style.zIndex = '';
        // Remove the marker so the element can be reused if needed
        delete element.dataset.dragHandlersAttached;
    };
};

const setupDraggable = async () => {
    if (!isDraggable.value || !toastId) return;

    // Clean up any existing observer
    if (mutationObserver) {
        mutationObserver.disconnect();
        mutationObserver = null;
    }

    // Wait for toast to be rendered in DOM
    await nextTick();

    // Try to find the toast element (search all containers - vue3-toastify uses one per position)
    const findToastElement = (): HTMLElement | null => {
        const allToasts = document.querySelectorAll('.Toastify__toast');
        if (!allToasts.length) return null;

        // Find a toast that:
        // 1. Should be draggable (has toast--draggable class)
        // 2. Doesn't have drag handlers attached yet
        // 3. Matches our instanceId or toastId if possible
        // 4. Is the most recently added draggable toast that doesn't have handlers

        // First, try to find by instanceId (most reliable)
        for (const toast of Array.from(allToasts)) {
            const toastEl = toast as HTMLElement;
            // Check if this toast matches our instanceId and should be draggable
            if (
                toastEl.getAttribute('data-toast-id') === instanceId.value &&
                toastEl.classList.contains('toast--draggable') &&
                toastEl.dataset.dragHandlersAttached !== 'true'
            ) {
                return toastEl;
            }
        }

        // Second, try to find by toastId
        if (toastId !== undefined) {
            for (const toast of Array.from(allToasts)) {
                const toastEl = toast as HTMLElement;
                // Check if this toast matches our ID, should be draggable, and doesn't have drag handlers
                if (
                    (toastEl.getAttribute('data-toast-id') === String(toastId) ||
                        toastEl.getAttribute('id') === String(toastId) ||
                        toastEl.dataset.toastId === String(toastId)) &&
                    toastEl.classList.contains('toast--draggable') &&
                    toastEl.dataset.dragHandlersAttached !== 'true'
                ) {
                    return toastEl;
                }
            }
        }

        // Fallback: Find the most recent draggable toast that doesn't have drag handlers
        // Only consider toasts that have the toast--draggable class
        for (let i = allToasts.length - 1; i >= 0; i--) {
            const toastEl = allToasts[i] as HTMLElement;
            if (
                toastEl.classList.contains('toast--draggable') &&
                toastEl.dataset.dragHandlersAttached !== 'true'
            ) {
                return toastEl;
            }
        }

        return null;
    };

    // Try to find immediately
    toastElement = findToastElement();

    if (toastElement) {
        // Set instanceId first to mark this toast as ours
        toastElement.setAttribute('data-toast-id', instanceId.value);
        makeToastDraggable(toastElement);
        setupProgressBarAccessibility(toastElement);
        return;
    }

    // If not found, use MutationObserver to watch for when the toast appears
    mutationObserver = new MutationObserver((mutations) => {
        // Check if any new toast elements were added (in any container)
        const hasNewToasts = mutations.some(mutation =>
            Array.from(mutation.addedNodes).some(node =>
                node instanceof HTMLElement &&
                node.classList.contains('Toastify__toast') &&
                node.classList.contains('toast--draggable')
            )
        );

        if (hasNewToasts) {
            const element = findToastElement();
            if (element && element !== toastElement) {
                toastElement = element;
                // Set instanceId immediately to mark this toast as ours
                toastElement.setAttribute('data-toast-id', instanceId.value);
                makeToastDraggable(toastElement);
                setupProgressBarAccessibility(toastElement);
                // Disconnect observer once we've found and set up the toast
                if (mutationObserver) {
                    mutationObserver.disconnect();
                    mutationObserver = null;
                }
            }
        }
    });

    // Observe body so we see toasts added to any position container
    mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
    });

    // Set a timeout to stop observing after a reasonable time
    setTimeout(() => {
        if (mutationObserver) {
            mutationObserver.disconnect();
            mutationObserver = null;
        }
        // Final attempt to find the element
        toastElement = findToastElement();
        if (toastElement) {
            toastElement.setAttribute('data-toast-id', instanceId.value);
            makeToastDraggable(toastElement);
            setupProgressBarAccessibility(toastElement);
        }
    }, 2000);
};

const showToast = async () => {
    // Clean up previous drag handlers
    if (dragCleanup) {
        dragCleanup();
        dragCleanup = null;
    }

    // Dismiss previous toast if exists
    if (toastId !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (toast as any).remove(toastId);
        toastId = undefined;
        toastElement = null;
    }

    const toastOptions = {
        type: getToastType(),
        toastId: instanceId.value, // Use unique instance ID to identify this toast
        dangerouslyHTMLString: true,
        closeOnClick: false,
        pauseOnHover: true,
        pauseOnFocusLoss: true,
        closeButton: props.closable ? getCloseButton : false,
        autoClose: props.autoClose ?? (props.closable ? false : 5000),
        hideProgressBar: false,
        position: props.position,
        rtl: appStore.dir === 'rtl',
        transition: props.animation,
        toastClassName: `toast--${getSize()} toast--${props.type}${isDraggable.value ? ' toast--draggable' : ''}${shouldHaveDefaultCursor.value ? ' toast--default-cursor' : ''}${shouldCenterIcon.value ? ' toast--icon-centered' : ''}`,
        bodyClassName: `toast-body--${getSize()}`,
        progressClassName: `toast-progress--${getSize()}`,
        icon: getIcon(),
        onClose: () => {
            if (dragCleanup) {
                dragCleanup();
                dragCleanup = null;
            }
            if (mutationObserver) {
                mutationObserver.disconnect();
                mutationObserver = null;
            }
            emit('close');
            toastId = undefined;
            toastElement = null;
        }
    };

    toastId = toast(getToastContent(), toastOptions);

    // Setup draggable after toast is created
    await setupDraggable();

    // Ensure toastElement is set and has the correct ID attribute
    await nextTick();
    if (!toastElement && isDraggable.value) {
        const allToasts = document.querySelectorAll('.Toastify__toast');
        // Find the most recent draggable toast that matches our instanceId or doesn't have handlers yet
        for (let i = allToasts.length - 1; i >= 0; i--) {
            const toastEl = allToasts[i] as HTMLElement;
            // Only consider draggable toasts
            if (
                toastEl.classList.contains('toast--draggable') &&
                (toastEl.getAttribute('data-toast-id') === instanceId.value ||
                    toastEl.dataset.dragHandlersAttached !== 'true')
            ) {
                toastElement = toastEl;
                toastElement.setAttribute('data-toast-id', instanceId.value);
                // If it doesn't have drag handlers yet, set them up
                if (toastElement.dataset.dragHandlersAttached !== 'true') {
                    makeToastDraggable(toastElement);
                }
                setupProgressBarAccessibility(toastElement);
                break;
            }
        }
    }
};

onMounted(() => {
    showToast();
});

onBeforeUnmount(() => {
    // Clean up progress bar observer
    if (progressBarObserver) {
        progressBarObserver.disconnect();
        progressBarObserver = null;
    }

    // Clean up mutation observer
    if (mutationObserver) {
        mutationObserver.disconnect();
        mutationObserver = null;
    }

    // Clean up drag handlers
    if (dragCleanup) {
        dragCleanup();
        dragCleanup = null;
    }

    // Clean up toast when component is unmounted
    if (toastId !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (toast as any).remove(toastId);
        toastId = undefined;
    }
    toastElement = null;
});

// Watch for prop changes and update toast
watch(
    () => [props.title, props.type, props.description, props.closable, props.autoClose, props.position, props.draggable, props.iconClass, appStore.isMobile],
    () => {
        showToast();
    },
    { deep: true },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1))
}
}

})